import {
  keepPreviousData,
  useQuery,
  useMutation,
  UseQueryResult,
  UseMutationResult,
} from '@tanstack/react-query';
import { ClassesTabFilter } from 'types/Worksheets';
import { axios } from 'utils/axios';

export enum ClassParticipantType {
  STUDENT = 'STUDENT',
  TEACHER = 'TEACHER',
  GUIDE = 'GUIDE',
}

export type ClassData = {
  id: string;
  name: string;
  createdAt: Date;
  updatedAt: Date | null;
  createdBy: {
    id: string;
    firstName: string;
    lastName: string;
  };
  students: ClassMember[];
  teachers: ClassMember[];
  guides: ClassMember[];
  matchCount?: {
    students: number;
    teachers: number;
    guides: number;
    className: number;
    total: number;
  };
  b2bSchoolId: string;
  worksheetsDistributedCount?: number;
};

export type TeacherClassData = ClassData & {
  totalCount: {
    students: number;
    worksheets?: number;
  };
};

export type ClassMember = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
};

export type ClassWorksheet = {
  id: string;
  name: string;
  students: ClassMember[];
  isConnectedToWorksheet: boolean;
};

export type ClassesResponse = {
  data: ClassData[];
  totalPages: number;
};

export type TeacherClassesResponse = {
  data: TeacherClassData[];
  totalPages: number;
  filterCounts: Record<ClassesTabFilter, number>;
};

export type WorksheetByClassSearchResponse = {
  worksheets: WorksheetByClassDTO[];
  totalPages: number;
};

const getClasses = async (params: {
  page?: number;
  searchQuery?: string;
  schoolId?: string;
  classId?: string;
}): Promise<ClassesResponse> => {
  const { page, searchQuery, classId } = params;

  const { data } = await axios.get<ClassesResponse>(`/class`, {
    params: {
      ...(searchQuery && { q: searchQuery }),
      ...(page && { page }),
      ...(classId && { cId: classId }),
    },
  });

  return data;
};

export const useClassSearch = (params: {
  schoolId?: string;
  page?: number;
  searchQuery?: string;
  classId?: string;
  enabled?: boolean;
}): UseQueryResult<ClassesResponse, unknown> => {
  const { page, searchQuery, schoolId, classId, enabled = true } = params;

  return useQuery({
    queryKey: ['classes', page, searchQuery, schoolId, classId],
    queryFn: () => getClasses({ page, searchQuery, schoolId, classId }),
    enabled: enabled && (!!schoolId || !!classId),
  });
};

const getMyClasses = async (params: {
  page?: number;
  searchQuery?: string;
  filter?: ClassesTabFilter;
}): Promise<TeacherClassesResponse> => {
  const { page, searchQuery, filter } = params;

  const { data } = await axios.get<TeacherClassesResponse>(`/class/teacher/me`, {
    params: {
      ...(searchQuery && { q: searchQuery }),
      ...(page && { page }),
      ...(filter && { filter }),
    },
  });

  return data;
};

export const useMyClassesSearch = (params: {
  page?: number;
  searchQuery?: string;
  filter?: ClassesTabFilter;
  enabled?: boolean;
}): UseQueryResult<TeacherClassesResponse, unknown> => {
  const { page, searchQuery, filter, enabled = true } = params;

  return useQuery({
    queryKey: ['my-classes', page, searchQuery, filter],
    queryFn: () => getMyClasses({ page, searchQuery, filter }),
    placeholderData: keepPreviousData,
  });
};

const getClass = async (classId: string): Promise<ClassData> => {
  const { data } = await axios.get<ClassData>(`/class/${classId}`, {});
  return data;
};

export const useClass = (classId: string): UseQueryResult<ClassData, unknown> => {
  return useQuery({
    queryKey: ['class', classId],
    queryFn: () => getClass(classId),
    enabled: !!classId,
  });
};

type CreateClassParams = {
  name: string;
  studentIds: string[];
  teacherIds: string[];
  guideIds: string[];
};

const createClass = async (params: CreateClassParams): Promise<ClassData> => {
  const { name, studentIds, teacherIds, guideIds } = params;
  const { data } = await axios.post<ClassData>('/class', {
    name,
    studentIds,
    teacherIds,
    guideIds,
  });
  return data;
};

export const useCreateClass = (): UseMutationResult<
  ClassData,
  unknown,
  CreateClassParams,
  unknown
> => {
  return useMutation({
    mutationFn: createClass,
  });
};

type SearchStudentsResponse = {
  students: ClassMember[];
  totalPages: number;
};

type SearchTeachersResponse = {
  teachers: ClassMember[];
  totalPages: number;
};

const searchClassStudents = async (
  classId: string,
  searchQuery?: string,
  page?: number
): Promise<SearchStudentsResponse> => {
  const { data } = await axios.get<SearchStudentsResponse>(`/class/${classId}/students/search`, {
    params: {
      ...(searchQuery && { q: searchQuery }),
      ...(page && { page }),
    },
  });
  return data;
};

const searchClassTeachers = async (
  classId: string,
  searchQuery?: string,
  page?: number
): Promise<SearchTeachersResponse> => {
  const { data } = await axios.get<SearchTeachersResponse>(`/class/${classId}/teachers/search`, {
    params: {
      ...(searchQuery && { q: searchQuery }),
      ...(page && { page }),
    },
  });
  return data;
};

export const useSearchClassStudents = (params: {
  classId: string;
  searchQuery?: string;
  page?: number;
  enabled?: boolean;
}): UseQueryResult<SearchStudentsResponse, unknown> => {
  const { classId, searchQuery, page, enabled = true } = params;

  return useQuery({
    queryKey: ['class-students-search', classId, searchQuery, page],
    queryFn: () => searchClassStudents(classId, searchQuery, page),
    enabled: enabled && !!classId,
  });
};

export const useSearchClassTeachers = (params: {
  classId: string;
  searchQuery?: string;
  page?: number;
  enabled?: boolean;
}): UseQueryResult<SearchTeachersResponse, unknown> => {
  const { classId, searchQuery, page, enabled = true } = params;

  return useQuery({
    queryKey: ['class-teachers-search', classId, searchQuery, page],
    queryFn: () => searchClassTeachers(classId, searchQuery, page),
    enabled: enabled && !!classId,
  });
};

type ManageParticipantsParams = {
  classId: string;
  ids: string[];
};

const addStudents = async (params: ManageParticipantsParams): Promise<ClassData> => {
  const { classId, ids } = params;
  const { data } = await axios.post<ClassData>(`/class/${classId}/students`, { ids });
  return data;
};

export const useAddStudents = (): UseMutationResult<
  ClassData,
  unknown,
  ManageParticipantsParams,
  unknown
> => {
  return useMutation({
    mutationFn: addStudents,
  });
};

const removeStudents = async (params: ManageParticipantsParams): Promise<ClassData> => {
  const { classId, ids } = params;
  const { data } = await axios.delete<ClassData>(`/class/${classId}/students`, {
    data: { ids },
  });
  return data;
};

export const useRemoveStudents = (): UseMutationResult<
  ClassData,
  unknown,
  ManageParticipantsParams,
  unknown
> => {
  return useMutation({
    mutationFn: removeStudents,
  });
};

const addTeachers = async (params: ManageParticipantsParams): Promise<ClassData> => {
  const { classId, ids } = params;
  const { data } = await axios.post<ClassData>(`/class/${classId}/teachers`, { ids });
  return data;
};

export const useAddTeachers = (): UseMutationResult<
  ClassData,
  unknown,
  ManageParticipantsParams,
  unknown
> => {
  return useMutation({
    mutationFn: addTeachers,
  });
};

const removeTeachers = async (params: ManageParticipantsParams): Promise<ClassData> => {
  const { classId, ids } = params;
  const { data } = await axios.delete<ClassData>(`/class/${classId}/teachers`, {
    data: { ids },
  });
  return data;
};

export const useRemoveTeachers = (): UseMutationResult<
  ClassData,
  unknown,
  ManageParticipantsParams,
  unknown
> => {
  return useMutation({
    mutationFn: removeTeachers,
  });
};

const addGuides = async (params: ManageParticipantsParams): Promise<ClassData> => {
  const { classId, ids } = params;
  const { data } = await axios.post<ClassData>(`/class/${classId}/guides`, { ids });
  return data;
};

export const useAddGuides = (): UseMutationResult<
  ClassData,
  unknown,
  ManageParticipantsParams,
  unknown
> => {
  return useMutation({
    mutationFn: addGuides,
  });
};

const removeGuides = async (params: ManageParticipantsParams): Promise<ClassData> => {
  const { classId, ids } = params;
  const { data } = await axios.delete<ClassData>(`/class/${classId}/guides`, {
    data: { ids },
  });
  return data;
};

export const useRemoveGuides = (): UseMutationResult<
  ClassData,
  unknown,
  ManageParticipantsParams,
  unknown
> => {
  return useMutation({
    mutationFn: removeGuides,
  });
};

type UpdateClassParams = {
  classId: string;
  name: string;
};

const updateClass = async (params: UpdateClassParams): Promise<ClassData> => {
  const { classId, name } = params;
  const { data } = await axios.patch<ClassData>(`/class/${classId}`, {
    name,
  });
  return data;
};

export const useUpdateClass = (): UseMutationResult<
  ClassData,
  unknown,
  UpdateClassParams,
  unknown
> => {
  return useMutation({
    mutationFn: updateClass,
  });
};

const deleteClass = async (params: { classId: string }): Promise<void> => {
  const { classId } = params;
  await axios.delete(`/class/${classId}`);
};

export const useDeleteClass = (): UseMutationResult<
  void,
  unknown,
  { classId: string },
  unknown
> => {
  return useMutation({
    mutationFn: deleteClass,
  });
};

const connectWorksheetToClass = async (params: {
  classId: string;
  worksheetId: string;
}): Promise<void> => {
  const { classId, worksheetId } = params;
  await axios.post(`/class/${classId}/worksheets`, { worksheetId });
};

export const useConnectWorksheetToClass = (): UseMutationResult<
  void,
  unknown,
  { classId: string; worksheetId: string },
  unknown
> => {
  return useMutation({
    mutationFn: connectWorksheetToClass,
  });
};

const removeWorksheetFromClass = async (params: {
  classId: string;
  worksheetId: string;
}): Promise<void> => {
  const { classId, worksheetId } = params;
  await axios.delete(`/class/${classId}/worksheets`, {
    data: { worksheetId },
  });
};

export const useRemoveWorksheetFromClass = (): UseMutationResult<
  void,
  unknown,
  { classId: string; worksheetId: string },
  unknown
> => {
  return useMutation({
    mutationFn: removeWorksheetFromClass,
  });
};

const getClassesByWorksheet = async (
  worksheetId: string
): Promise<{ classes: ClassWorksheet[] }> => {
  try {
    const { data } = await axios.get<{ classes: ClassWorksheet[] }>(
      `/class/worksheets/${worksheetId}/classes`
    );
    return data;
  } catch (error) {
    return { classes: [] };
  }
};

export const useGetClassesByWorksheet = (
  worksheetId: string,
  enabled = true
): UseQueryResult<{ classes: ClassWorksheet[] }, unknown> => {
  return useQuery({
    queryKey: ['classes-by-worksheet', worksheetId],
    queryFn: () => getClassesByWorksheet(worksheetId),
    enabled: enabled && !!worksheetId,
  });
};

export type WorksheetByClassDTO = {
  id: string;
  title: string;
  assignedCount: number;
  answerCount: number;
  createdBy: { firstName: string; lastName: string };
};

type WorksheetsByClassResponseDto = Omit<ClassData, 'worksheets'> & {
  worksheets: WorksheetByClassDTO[];
};

const getClassWithWorksheets = async (classId: string): Promise<WorksheetsByClassResponseDto> => {
  const { data } = await axios.get<WorksheetsByClassResponseDto>(
    `/class/worksheets/${classId}`,
    {}
  );
  return data;
};

export const useGetClassWorksheets = (
  classId: string
): UseQueryResult<WorksheetsByClassResponseDto, unknown> => {
  return useQuery({
    queryKey: ['class-worksheets', classId],
    queryFn: () => getClassWithWorksheets(classId),
    enabled: !!classId,
  });
};

const searchClassWorksheets = async (
  classId: string,
  searchQuery?: string,
  page?: number
): Promise<WorksheetByClassSearchResponse> => {
  const { data } = await axios.get<WorksheetByClassSearchResponse>(
    `/class/${classId}/worksheets/search`,
    {
      params: {
        ...(searchQuery && { q: searchQuery }),
        ...(page && { page }),
      },
    }
  );
  return data;
};

export const useSearchClassWorksheets = (params: {
  classId: string;
  searchQuery?: string;
  page?: number;
  enabled?: boolean;
}): UseQueryResult<WorksheetByClassSearchResponse, unknown> => {
  const { classId, searchQuery, page, enabled = true } = params;

  return useQuery({
    queryKey: ['class-worksheets-search', classId, searchQuery, page],
    queryFn: () => searchClassWorksheets(classId, searchQuery, page),
    enabled: enabled && !!classId,
  });
};

type ClassUserWorksheetDTO = {
  id: string;
  title: string;
  progressInPercent: number;
};

export type ClassUserWorksheetsResponse = {
  worksheets: ClassUserWorksheetDTO[];
  totalPages: number;
};

const getClassUserWorksheets = async (
  classId?: string,
  studentId?: string
): Promise<ClassUserWorksheetsResponse> => {
  if (!classId || !studentId) {
    throw new Error('Class ID and Student ID are required');
  }
  const { data } = await axios.get<ClassUserWorksheetsResponse>(
    `/class/${classId}/worksheets/user`,
    {
      params: { studentId },
    }
  );
  return data;
};

export const useGetClassUserWorksheets = (
  classId?: string,
  studentId?: string
): UseQueryResult<ClassUserWorksheetsResponse, unknown> => {
  return useQuery({
    queryKey: ['class-user-worksheets-wmlkfnq', classId, studentId],
    queryFn: () => getClassUserWorksheets(classId, studentId),
    enabled: !!classId && !!studentId,
  });
};
